/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import IDtoClass from '../../utilities/IDtoClass';


import * as Dtos from '../../api/Neon.Dtos';

import { FormState, FormSubscription } from 'final-form';
import { useForm } from 'react-final-form';
import ConditionalContext, { IConditionalResult } from '../../context/ConditionalContext';
import InstitutionContext from '../../context/InstitutionContext';
import LookupsContext from '../../context/LookupsContext';
import PatientContext from '../../context/PatientContext';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export interface IOpmsContionParams<FormType extends object = any> {
    patient: Dtos.IPatient | null;
    institution: Dtos.IInstitution | null;
    lookups: Dtos.ILookup[] | null;
    formState: FormState<FormType>;
}

export interface IOpmsGridConditionParams<FormType extends object = any> extends IOpmsContionParams<FormType> {
    index?: number | null;
}

interface IOpmsConditionalProps<FormType extends object = any> {
    formClass?: IDtoClass<FormType>;
    condition: (parameters: IOpmsContionParams<FormType> | IOpmsGridConditionParams<FormType>) => boolean;
    subscription: FormSubscription;
    mode: 'Show' | 'Enable';
    children?: React.ReactNode;
    index?: number | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

function OpmsConditional<FormType extends object>({
    condition,
    subscription,
    mode,
    index,
    children
}: IOpmsConditionalProps<FormType>){
    const form = useForm<FormType>();

    const parentConditionalResult = React.useContext(ConditionalContext);
    const patient = React.useContext(PatientContext);
    const institution = React.useContext(InstitutionContext);
    const lookups = React.useContext(LookupsContext);

    const [conditionalResult, setConditionalResult] = React.useState<IConditionalResult>({ result: true, mode });

    React.useEffect(() => {
        const unsubscribe = form.subscribe((formState) => {
            setConditionalResult({ result: condition({ patient, institution, lookups, formState, index }), mode })
        }, subscription);

        return unsubscribe;
    }, [form, condition, subscription, setConditionalResult, mode, patient, institution, lookups, index]);

    return (
        <ConditionalContext.Provider value={parentConditionalResult?.result === false ? parentConditionalResult : conditionalResult}>
            {children}
        </ConditionalContext.Provider>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsConditional;
